//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import { packageData } from 'utils/ErrorUtils';
import { makeUrl, replaceToken } from 'utils/UrlUtil';
import DataUrlConstants from 'utils/DataUrlConstants';

import { CardType } from 'pages/business-studio/business-dashboard/types';

import { injectEndpoints } from '../api';
import {
  BookmarkBusinessStudioFilterParams,
  BusinessEntity,
  BusinessStudioEntityFilter,
  BusinessStudioFiltersResponse,
  BusinessStudioQueue,
  BusinessStudioRecordsResponse,
  CreateBusinessStudioFilterParams,
  CreateBusinessStudioRecordParams,
  CreateEntityLayoutParams,
  CurrencyOption,
  DeleteBusinessStudioFilterParams,
  DeleteEntityLayoutParams,
  DeleteRecordParams,
  EntityLayoutResponse,
  EntityReference,
  EntitySchemaResponse,
  GetBusinessStudioFiltersParams,
  GetBusinessStudioQueueRecordsParams,
  GetBusinessStudioRecordsParams,
  GetRecordParams,
  RecordResponse,
  UpdateBusinessStudioFilterParams,
  UpdateBusinessStudioRecordParams,
  UpdateEntityLayoutParams,
} from './types';
import { Entity } from 'store/entity/types';

const businessStudioApi = injectEndpoints({
  endpoints: (builder) => ({
    // 1. Get entities for dropdown
    // Using Business Studio API endpoint
    getBusinessStudioEntities: builder.query<BusinessEntity[], void>({
      query: () => ({
        url: 'arcade/api/v1/studio/business/entities',
      }),
      // Business Studio API returns entities array directly, not wrapped in an object
      providesTags: ['BusinessStudioEntity'],
    }),

    // 1b. Get queue tabs for an entity (dynamic tabs after "Golden Records")
    getBusinessStudioQueues: builder.query<BusinessStudioQueue[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queues', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: `queues-${entityId}` }],
    }),

    // 2. Get entity layouts by entity ID
    getBusinessStudioEntityLayouts: builder.query<EntityLayoutResponse[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/layouts', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioLayout', id: entityId }],
    }),

    // 2b. Get entity schema with fields by entity ID
    getBusinessStudioEntitySchema: builder.query<EntitySchemaResponse, string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/schema/entity/{entityId}', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: entityId }],
    }),

    // 2c. Get full entity definition (including picklist field values) for a single entity
    getBusinessStudioEntityDefinition: builder.query<Entity, string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/schema/entity/{entityId}', { entityId }),
      }),
      // The /api/v1/schema/entity endpoint wraps the entity under data.entities[0]
      transformResponse: (response: { entities: Entity[] }) => response.entities[0],
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: `definition-${entityId}` }],
    }),

    // 3. Get records for selected entity (for grid)
    // Using Business Studio API endpoint
    getBusinessStudioEntityRecords: builder.query<BusinessStudioRecordsResponse, GetBusinessStudioRecordsParams>({
      query: ({ entityId, predicate, cursor, count = 25, direction = 'next', orderBy, sortDirection, page }) => {
        const preparedPredicate = predicate ? packageData(predicate) : undefined;

        return {
          url: makeUrl(
            `arcade/api/v1/studio/business/entity/{entityId}/records`,
            { entityId },
            {
              cursor,
              count,
              direction,
              orderBy,
              sortDirection,
              page,
            }
          ),
          method: 'POST',
          body: preparedPredicate,
          headers: {
            'Content-Type': 'text/plain',
          },
        };
      },
      providesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.entityId }],
    }),

    // 3b. Get records for a specific queue tab
    getBusinessStudioQueueRecords: builder.query<BusinessStudioRecordsResponse, GetBusinessStudioQueueRecordsParams>({
      query: ({ entityId, queueId, cursor, count = 50, direction = 'NEXT', orderBy, sortDirection }) => ({
        url: makeUrl(
          `api/v1/studio/business/entity/{entityId}/queue-records`,
          { entityId },
          {
            queueId,
            cursor,
            count,
            direction,
            ...(orderBy && { orderBy }),
            ...(sortDirection && { sortDirection }),
          }
        ),
        method: 'POST',
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueId}` },
      ],
    }),

    // 4. Get single record by ID
    getBusinessStudioRecord: builder.query<RecordResponse, GetRecordParams>({
      query: ({ entityId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', {
          entityId,
          recordId,
        }),
      }),
      providesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.recordId }],
    }),

    // 5. Get entity references for Table Card configuration
    // Used by the Add/Edit Table Card form to list available related entities
    getBusinessStudioEntityReferences: builder.query<EntityReference[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/references', { entityId }),
      }),
    }),

    // 6a. Create a new record
    createBusinessStudioRecord: builder.mutation<RecordResponse, CreateBusinessStudioRecordParams>({
      query: ({ entityId, body, ignoreWarnings = false }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/record', { entityId }),
        method: 'POST',
        body,
        params: { ignoreWarnings },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.entityId }],
    }),

    // 6b. Update an existing record
    updateBusinessStudioRecord: builder.mutation<RecordResponse, UpdateBusinessStudioRecordParams>({
      query: ({ entityId, recordId, body, ignoreWarnings = false }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', { entityId, recordId }),
        method: 'PUT',
        body,
        params: { ignoreWarnings },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioRecord', id: arg.recordId },
        { type: 'BusinessStudioRecord', id: arg.entityId },
      ],
    }),

    // 6. Delete record by ID
    deleteBusinessStudioRecord: builder.mutation<void, DeleteRecordParams>({
      query: ({ entityId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', {
          entityId,
          recordId,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioRecord', id: arg.recordId },
        { type: 'BusinessStudioRecord', id: arg.entityId },
      ],
    }),

    // 7. Get single layout by layoutId
    getEntityLayoutById: builder.query<EntityLayoutResponse, { entityId: string; layoutId: string; recordId: string }>({
      query: ({ layoutId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', { layoutId }, { recordId }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 7. Create new dashboard layout for entity
    createEntityLayout: builder.mutation<EntityLayoutResponse, CreateEntityLayoutParams>({
      query: ({ entityId, entityName, name, displayName, description, isDefault, status, cards, cardPositions }) => {
        const payload = {
          entityId,
          entityName,
          name,
          displayName,
          description,
          isDefault,
          status,
          cards,
          cardPositions,
        };

        return {
          url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/layout', { entityId }),
          method: 'POST',
          body: payload,
        };
      },
      // Invalidate the entity-level layouts list so it includes the newly created layout
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioLayout', id: arg.entityId }],
    }),

    // 8. Update existing dashboard layout
    updateEntityLayout: builder.mutation<EntityLayoutResponse, UpdateEntityLayoutParams>({
      query: ({
        entityId,
        layoutId,
        entityName,
        name,
        displayName,
        description,
        isDefault,
        status,
        cards,
        cardPositions,
      }) => {
        const payload = {
          entityId,
          entityName,
          name,
          displayName,
          description,
          isDefault,
          status,
          cards,
          cardPositions,
        };

        return {
          url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', {
            layoutId,
          }),
          method: 'PUT',
          body: payload,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 9. Delete dashboard layout
    deleteEntityLayout: builder.mutation<void, DeleteEntityLayoutParams>({
      query: ({ layoutId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', {
          layoutId,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 10. Get filters for Business Studio
    getBusinessStudioFilters: builder.query<BusinessStudioFiltersResponse, GetBusinessStudioFiltersParams>({
      query: ({ entityId = null, bookmarked, cursor, count = 100, direction = 'next', orderBy, sortDirection }) => ({
        url: makeUrl(DataUrlConstants.GET_BUSINESS_STUDIO_FILTERS, null, {
          entityId,
          cursor,
          count,
          direction,
          bookmarked,
          ...(orderBy && { orderBy }),
          ...(sortDirection && { sortDirection }),
        }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioFilter', id: arg.entityId || 'LIST' },
        ...(result?.filters || []).map((filter) => ({ type: 'BusinessStudioFilter' as const, id: filter.id })),
      ],
    }),

    // 11. Create filter for Business Studio
    createBusinessStudioFilter: builder.mutation<BusinessStudioEntityFilter, CreateBusinessStudioFilterParams>({
      query: (params) => ({
        url: DataUrlConstants.CREATE_BUSINESS_STUDIO_FILTER,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.syncariEntityId }],
    }),

    // 12. Update filter for Business Studio
    updateBusinessStudioFilter: builder.mutation<BusinessStudioEntityFilter, UpdateBusinessStudioFilterParams>({
      query: ({ filterId, filter }) => ({
        url: replaceToken(DataUrlConstants.UPDATE_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'PUT',
        body: filter,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioFilter', id: arg.filterId },
        { type: 'BusinessStudioFilter', id: arg.filter.syncariEntityId },
      ],
    }),

    // 13. Delete filter for Business Studio
    deleteBusinessStudioFilter: builder.mutation<void, DeleteBusinessStudioFilterParams>({
      query: ({ filterId }) => ({
        url: replaceToken(DataUrlConstants.DELETE_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.filterId }],
    }),

    // 14. Get supported currencies for the currency formatter
    // commonFirst=true puts the most commonly used currencies at the top of the list
    getCurrencies: builder.query<CurrencyOption[], void>({
      query: () => ({
        url: 'arcade/api/v1/currencies?commonFirst=true',
      }),
    }),

    // 15. Bookmark/unbookmark filter for Business Studio
    bookmarkBusinessStudioFilter: builder.mutation<void, BookmarkBusinessStudioFilterParams>({
      query: ({ filterId, bookmarked }) => ({
        url: replaceToken(DataUrlConstants.BOOKMARK_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'PATCH',
        body: bookmarked,
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.filterId }],
    }),
  }),
});

export const {
  useGetBusinessStudioQueuesQuery,
  useLazyGetBusinessStudioQueuesQuery,
  useGetBusinessStudioQueueRecordsQuery,
  useLazyGetBusinessStudioQueueRecordsQuery,
  useCreateBusinessStudioRecordMutation,
  useUpdateBusinessStudioRecordMutation,
  useGetCurrenciesQuery,
  useGetBusinessStudioEntitiesQuery,
  useLazyGetBusinessStudioEntitiesQuery,
  useGetBusinessStudioEntityLayoutsQuery,
  useLazyGetBusinessStudioEntityLayoutsQuery,
  useGetBusinessStudioEntitySchemaQuery,
  useLazyGetBusinessStudioEntitySchemaQuery,
  useGetBusinessStudioEntityDefinitionQuery,
  useGetBusinessStudioEntityRecordsQuery,
  useLazyGetBusinessStudioEntityRecordsQuery,
  useGetBusinessStudioRecordQuery,
  useLazyGetBusinessStudioRecordQuery,
  useGetBusinessStudioEntityReferencesQuery,
  useLazyGetBusinessStudioEntityReferencesQuery,
  useDeleteBusinessStudioRecordMutation,
  useGetEntityLayoutByIdQuery,
  useLazyGetEntityLayoutByIdQuery,
  useCreateEntityLayoutMutation,
  useUpdateEntityLayoutMutation,
  useDeleteEntityLayoutMutation,
  useGetBusinessStudioFiltersQuery,
  useLazyGetBusinessStudioFiltersQuery,
  useCreateBusinessStudioFilterMutation,
  useUpdateBusinessStudioFilterMutation,
  useDeleteBusinessStudioFilterMutation,
  useBookmarkBusinessStudioFilterMutation,
  util: businessStudioApiUtil,
} = businessStudioApi;
